import React from 'react';
import { MapPin } from 'lucide-react';

/* ------------------------------------------------------------------
   Flat (equirectangular) world map, drawn as a dot grid.

   Everything below is computed once at module load — no state, no
   timers, no transitions. The map is completely static.

   LAND_ROWS is a 180 x 69 land/water mask sampled every 2 degrees from
   Natural Earth 110m land data. Each string is one row of run-length
   counts, alternating water/land and starting with water.
------------------------------------------------------------------ */

const STEP = 2;                       // degrees per cell
const COLS = 180;                     // 360 / STEP
const LAT_TOP = 80;
const LAT_BOTTOM = -58;
const SCALE = 3;                      // px per degree
const CELL = STEP * SCALE;            // 6px between dots
const MAP_W = 360 * SCALE;            // 1080
const MAP_H = (LAT_TOP - LAT_BOTTOM) * SCALE; // 414

const px = (lon) => (lon + 180) * SCALE;
const py = (lat) => (LAT_TOP - lat) * SCALE;

const LAND_ROWS = [
  '42,13,2,25,16,1,1,4,33,3',
  '34,2,4,1,7,4,2,27,16,3,1,1,38,1',
  '29,1,2,2,2,1,2,3,2,1,15,21,39,4,13,10,12,3',
  '28,4,11,2,17,18,38,2,14,12',
  '28,2,1,5,1,1,2,2,3,8,10,16,37,2,7,2,2,26,5,5',
  '9,10,6,2,5,8,1,4,3,9,6,16,22,5,18,46',
  '0,2,6,25,3,3,2,1,1,3,1,3,2,1,1,4,6,10,24,13,2,1,2,11,1,52',
  '0,6,1,40,6,3,1,2,5,8,7,1,1,2,14,11,3,69',
  '10,36,1,3,4,4,7,5,9,3,13,6,1,78',
  '8,36,6,4,12,3,24,6,2,71,1,5',
  '7,7,1,1,3,24,9,4,37,7,2,1,2,63,3,2',
  '12,1,10,21,8,7,29,1,7,3,4,58,8,3',
  '10,1,13,23,5,8,28,1,6,3,3,58,9,4',
  '24,25,2,11,24,1,2,1,5,2,1,61,1,1,7,3',
  '27,23,1,12,23,1,1,3,2,69,7,1',
  '26,31,5,1,28,70,1,1',
  '28,30,3,3,24,72,1,1',
  '29,30,1,1,29,16,1,1,1,50',
  '28,28,1,1,32,7,1,7,5,48,3,2',
  '28,28,30,6,4,2,2,4,3,1,3,45,4,1',
  '28,25,33,5,7,1,1,3,1,46,1,3,6,1',
  '29,23,34,4,7,2,2,2,1,46,3,2,5,1',
  '30,23,38,5,13,42,3,1,3,3',
  '31,21,35,9,12,43,5,3',
  '32,18,36,12,2,3,5,43',
  '34,12,3,1,36,29,1,35',
  '33,1,1,7,7,1,34,31,1,35',
  '34,1,1,6,41,25,1,7,3,31',
  '35,1,1,5,41,25,2,9,5,25',
  '38,4,6,1,1,2,30,27,1,10,5,22',
  '38,4,3,2,5,1,29,27,2,8,8,7,3,7,1,1',
  '39,7,11,1,24,28,1,8,8,5,6,6,7,1',
  '42,4,1,1,34,28,2,5,10,4,8,6,5,1',
  '45,4,33,29,1,3,13,3,9,5,6,1',
  '47,2,5,2,26,30,16,2,12,3',
  '48,1,4,6,24,33,13,1,13,1',
  '51,10,23,32,14,1,9,1,11,2',
  '52,10,23,6,2,22,26,1',
  '52,13,30,19,25,1,1,1,5,2',
  '51,14,30,18,27,1,1,1,3,3',
  '50,15,30,17,28,2,3,4,1,1,3,1',
  '50,18,27,16,30,2,3,3,1,1,6,1,1,1',
  '50,21,25,14,32,1,7,2,6,5',
  '50,23,24,13,33,1,16,4,1,1',
  '51,22,24,13,36,2,11,3,1,2',
  '51,22,24,13,40,1,1,1,11,2',
  '52,20,25,14,46,2,2,1',
  '52,19,26,14,3,2,39,3,3,1',
  '53,18,25,15,2,2,38,6,2,2',
  '55,16,25,13,4,2,37,12,15,1',
  '55,15,27,11,5,2,35,15',
  '55,15,28,10,4,3,33,17,8,1',
  '55,12,31,10,4,2,33,19',
  '55,11,32,9,40,20',
  '55,11,32,9,41,19',
  '55,10,34,7,42,19',
  '55,10,35,5,43,7,2,10',
  '55,9,36,2,46,2,8,8',
  '54,8,98,6,11,1',
  '54,8,99,3,14,2',
  '54,5,119,1',
  '53,5,105,2,11,2',
  '54,4,117,2',
  '53,4,117,2',
  '53,5',
  '53,3',
  '53,3',
  '54,3',
];

// One <path> of zero-length segments; round line caps render them as dots.
const landDots = (() => {
  let d = '';
  LAND_ROWS.forEach((row, rowIndex) => {
    const y = rowIndex * CELL;
    let col = 0;
    let land = false;
    row.split(',').forEach((run) => {
      const n = Number(run);
      if (land) {
        for (let k = 0; k < n && col + k < COLS; k++) d += `M${(col + k) * CELL} ${y}h0`;
      }
      col += n;
      land = !land;
    });
  });
  return d;
})();

/* ------------------------------------------------------------------
   Locations
------------------------------------------------------------------ */

const locations = [
  {
    country: 'Sharjah, UAE',
    role: 'Headquarters',
    detail: 'Hamriyah Free Zone Phase 2, Sharjah',
    lat: 25.3463,
    lon: 55.4209,
    primary: true,
    box: { x: 738, y: 116, w: 112 },
  },
  {
    country: 'Oman',
    role: 'Branch Office',
    detail: 'Muscat, Sultanate of Oman',
    lat: 23.588,
    lon: 58.3829,
    box: { x: 748, y: 196, w: 112 },
  },
  {
    country: 'Canada',
    role: 'Subsidiary',
    detail: 'North America operations',
    lat: 56.13,
    lon: -106.35,
    box: { x: 252, y: 34, w: 96 },
  },
  {
    country: 'USA',
    role: 'Subsidiary',
    detail: 'North America operations',
    lat: 39.5,
    lon: -98.35,
    box: { x: 122, y: 132, w: 96 },
  },
];

const BOX_H = 36;

const pins = locations.map((loc) => {
  const x = px(loc.lon);
  const y = py(loc.lat);
  const { box } = loc;
  // Attach the leader to whichever side of the label faces the pin.
  const anchorX = x < box.x ? box.x : box.x + box.w;
  const anchorY = box.y + BOX_H / 2;
  return { ...loc, x, y, anchorX, anchorY };
});

const BranchesSection = () => {
  return (
    <section className="py-20 lg:py-24 bg-[#0a1628]" id="branches">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10 lg:mb-12">
          <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
            Global Footprint
          </p>
          <h2 className="text-3xl lg:text-4xl font-light text-white">
            Our Branches &amp; <span className="font-semibold">Subsidiaries</span>
          </h2>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            Headquartered in Sharjah with a branch office in Oman and subsidiaries
            across North America.
          </p>
        </div>

        {/* World map */}
        <svg
          viewBox={`0 0 ${MAP_W} ${MAP_H}`}
          className="w-full h-auto"
          role="img"
          aria-label="World map showing Grenew locations in Sharjah, Oman, the USA and Canada"
          data-testid="branches-map"
        >
          {/* Land */}
          <path
            d={landDots}
            fill="none"
            stroke="rgba(79,209,197,0.32)"
            strokeWidth="2.6"
            strokeLinecap="round"
          />

          {/* Leader lines + labels. Hidden below lg, where the map is too
              narrow for the type to stay legible — the cards below carry
              the same information. */}
          <g className="hidden lg:block">
            {pins.map((pin) => (
              <g key={`label-${pin.country}`}>
                <line
                  x1={pin.x}
                  y1={pin.y}
                  x2={pin.anchorX}
                  y2={pin.anchorY}
                  stroke="rgba(79,209,197,0.5)"
                  strokeWidth="1"
                />
                <rect
                  x={pin.box.x}
                  y={pin.box.y}
                  width={pin.box.w}
                  height={BOX_H}
                  fill="rgba(10,22,40,0.92)"
                  stroke={pin.primary ? 'rgba(79,209,197,0.6)' : 'rgba(255,255,255,0.15)'}
                  strokeWidth="1"
                />
                <text
                  x={pin.box.x + 11}
                  y={pin.box.y + 16}
                  fill="#ffffff"
                  fontSize="13"
                  fontWeight="600"
                >
                  {pin.country}
                </text>
                <text
                  x={pin.box.x + 11}
                  y={pin.box.y + 29}
                  fill="#4fd1c5"
                  fontSize="9"
                  fontWeight="600"
                  letterSpacing="0.9"
                >
                  {pin.role.toUpperCase()}
                </text>
              </g>
            ))}
          </g>

          {/* Pins */}
          {pins.map((pin) => (
            <g key={`pin-${pin.country}`}>
              <circle
                cx={pin.x}
                cy={pin.y}
                r={pin.primary ? 11 : 9}
                fill="none"
                stroke="rgba(79,209,197,0.45)"
                strokeWidth="1.2"
              />
              <circle
                cx={pin.x}
                cy={pin.y}
                r={pin.primary ? 5.5 : 4.5}
                fill="#4fd1c5"
                stroke="#0a1628"
                strokeWidth="1.5"
              />
              {pin.primary && <circle cx={pin.x} cy={pin.y} r="2" fill="#0a1628" />}
            </g>
          ))}
        </svg>

        {/* Location cards */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10 lg:mt-12"
          data-testid="branches-list"
        >
          {locations.map((loc) => (
            <div
              key={loc.country}
              className={`flex items-start gap-3 p-5 border transition-colors ${
                loc.primary
                  ? 'bg-white/[0.06] border-[#4fd1c5]/40'
                  : 'bg-white/[0.03] border-white/10 hover:border-[#4fd1c5]/40'
              }`}
              data-testid={`branch-${loc.country.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            >
              <MapPin className="w-5 h-5 text-[#4fd1c5] flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <h3 className="text-white font-semibold">{loc.country}</h3>
                <span
                  className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 mt-1.5 ${
                    loc.primary ? 'bg-[#4fd1c5] text-[#0a1628]' : 'bg-white/10 text-[#4fd1c5]'
                  }`}
                >
                  {loc.role}
                </span>
                <p className="text-sm text-gray-400 mt-2">{loc.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BranchesSection;
